import { useState } from 'react';
import { initials } from '../../lib/format';
import NoteContent, { legacyToTipTap } from './NoteContent';
import NoteReactions from './NoteReactions';
import RichNoteEditorLazy from './RichNoteEditorLazy';
import './notes.css';

// One entry in the conversation. Three shapes:
//   · an ERP/audit entry (note.is_system) → a centred sliver, out of the way
//   · your own note   → bubble on the right
//   · everyone else's → bubble on the left
//
// The bubble is only the container: everything inside it — rich text, @mentions,
// /resource links, image grids, file chips — is rendered by the same components
// the feed used, at the same sizes. Bubbles run to 86% of the width rather than
// the usual chat 70% precisely so a formatted HQ instruction still reads.
//
// Bubbles carry no controls. Tapping one opens the ··· sheet, which is where
// react / reply / edit / delete live.

const AVATAR_COLORS = ['#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#fa8c16', '#13c2c2', '#f5222d', '#faad14', '#2f54eb', '#a0d911'];
const avatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const fileSize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const EVENT_ICONS = {
  stage: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 12h5l2-5 3 10 2-5h4" /></svg>,
  file: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M14 3v5h5" /><path d="M19 8v12H5V3h9Z" /></svg>,
  money: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 6v12M9 9h4.5a1.8 1.8 0 0 1 0 3.6h-3a1.8 1.8 0 0 0 0 3.6H15" /></svg>,
  status: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 8h13l-3-3M20 16H7l3 3" /></svg>,
};

/** Pick the event icon from what the entry is about. */
function eventIcon(text) {
  const t = (text || '').toLowerCase();
  // Stage lines are matched FIRST, on their exact shape, because the keyword
  // rules below are broad enough to steal them: 'Stage set to "Request receipt"
  // by the agent.' contains "receipt" and would otherwise draw the money icon.
  if (/^stage set to ".+" by the /.test(t)) return EVENT_ICONS.stage;
  if (t.includes('attached file') || t.includes('attachment')) return EVENT_ICONS.file;
  if (t.includes('receipt') || t.includes('commision') || t.includes('commission') || t.includes('rm ')) return EVENT_ICONS.money;
  if (t.includes('status')) return EVENT_ICONS.status;
  return EVENT_ICONS.stage;
}

/** ERP lines wrap their subjects in [brackets] — render those as the emphasis. */
function renderEventText(text) {
  const parts = String(text || '').split(/(\[[^\]]*\]|"[^"]*")/g).filter(Boolean);
  return parts.map((p, i) => (
    /^\[.*\]$/.test(p) || /^".*"$/.test(p)
      ? <b key={i}>{p.replace(/^[["]|[\]"]$/g, '')}</b>
      : <span key={i}>{p}</span>
  ));
}

/** One-line preview of the note being replied to, for the quote strip. */
function quotePreview(note) {
  const text = String(note?.content || '').replace(/\s+/g, ' ').trim();
  return text.length > 64 ? `${text.slice(0, 64)}…` : text;
}

export default function NoteItem({
  note,
  // The note this one replies to, if any — rendered as a quote strip inside the
  // bubble so a threaded reply still reads in a flat timeline.
  parent = null,
  // True when the previous entry was the same author moments earlier: drop the
  // avatar and name so a run of messages reads as one turn.
  grouped = false,
  // The parent owns which row is in edit mode (the ··· sheet starts it).
  editingId = null,
  mentionableUsers = [],
  mentionableResources = [],
  onSaveEdit,
  onCancelEdit,
  onOpenActions,
  onToggleReaction,
  onResourceClick,
  onOpenFile,
}) {
  const [draft, setDraft] = useState(null); // { json, text }
  const [saving, setSaving] = useState(false);

  // ── Centred sliver for machine-written entries ───────────────────────────
  if (note.is_system) {
    return (
      <div className="note-event" data-note-id={note.id}>
        <span className="note-event-icon">{eventIcon(note.content)}</span>
        <span className="note-event-text">
          {renderEventText(note.content)} <span className="note-event-when">· {note.when}</span>
        </span>
      </div>
    );
  }

  const editing = editingId === note.id;
  const author = note.who || note.author?.full_name || (note.author_type === 'AGENT' ? 'Agent' : 'HQ');
  const deleted = !!note.is_deleted;
  const mine = !!note.mine;

  const images = (note.attachments || []).filter((a) => a.mime_type?.startsWith('image/'));
  const files = (note.attachments || []).filter((a) => !a.mime_type?.startsWith('image/'));
  const hasReactions = Object.values(note.reactions || {}).some((r) => (r?.count ?? r) > 0);

  // Signed URLs are best-effort server-side — don't hand the viewer a null.
  const openAttachment = (a) => {
    const url = a.url || a.file_url;
    if (url) onOpenFile?.({ name: a.file_name, url });
  };

  const saveEdit = async () => {
    if (!draft?.text?.trim()) return;
    setSaving(true);
    try {
      await onSaveEdit?.(note.id, { content: draft.text.trim(), content_json: draft.json });
      setDraft(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`note-row${mine ? ' mine' : ''}${grouped ? ' grouped' : ''}${editing ? ' editing' : ''}`}
      data-note-id={note.id}
    >
      {grouped
        ? <span className="note-avatar-gap" aria-hidden="true" />
        : (
          <div className="note-avatar" style={{ background: deleted ? 'var(--muted)' : avatarColor(author) }}>
            {initials(author)}
          </div>
        )}

      <div className="note-stack">
        {!grouped && !mine && (
          <div className="note-who">
            {author}
            {!deleted && (
              <span className={`note-badge ${note.author_type === 'AGENT' ? 'agent' : 'admin'}`}>
                {note.author_type === 'AGENT' ? 'Agent' : 'HQ'}
              </span>
            )}
          </div>
        )}

        {editing ? (
          <>
            <RichNoteEditorLazy
              compact
              initialContent={note.content_json || legacyToTipTap(note.content)}
              placeholder="Edit your note…"
              mentionableUsers={mentionableUsers}
              mentionableResources={mentionableResources}
              autoFocus
              onChange={(json, text) => setDraft({ json, text })}
              onSubmit={saveEdit}
            />
            <div className="note-edit-actions">
              <button type="button" className="note-edit-btn go" disabled={saving} onClick={saveEdit}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="note-edit-btn" onClick={() => { setDraft(null); onCancelEdit?.(); }}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div
              className={`note-bubble${deleted ? ' deleted' : ''}`}
              onClick={() => !deleted && onOpenActions?.(note)}
              role={deleted ? undefined : 'button'}
              tabIndex={deleted ? undefined : 0}
              onKeyDown={(e) => { if (!deleted && e.key === 'Enter') onOpenActions?.(note); }}
            >
              {parent && (
                <div className="note-quote">
                  <b>{parent.mine ? 'You' : (parent.who || parent.author?.full_name || 'a note')}</b>
                  <span>{quotePreview(parent)}</span>
                </div>
              )}

              {deleted
                ? <div className="note-deleted">This note has been deleted</div>
                : <NoteContent note={note} onResourceClick={onResourceClick} />}

              {images.length > 0 && (
                <div className="note-images">
                  {images.map((a) => (
                    <img
                      key={a.id}
                      src={a.url || a.file_url}
                      alt={a.file_name}
                      onClick={(e) => { e.stopPropagation(); openAttachment(a); }}
                    />
                  ))}
                </div>
              )}

              {files.length > 0 && (
                <div className="note-files">
                  {files.map((a) => (
                    <div
                      key={a.id}
                      className="note-file"
                      onClick={(e) => { e.stopPropagation(); openAttachment(a); }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 3v5h5" /><path d="M19 8v12H5V3h9Z" /></svg>
                      <span className="note-file-name">{a.file_name}</span>
                      {a.file_size_bytes ? <span className="note-file-size">· {fileSize(a.file_size_bytes)}</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="note-meta">{note.when}{note.is_edited && !deleted ? ' · edited' : ''}</div>

            {hasReactions && !deleted && (
              <div className="note-docked">
                <NoteReactions
                  reactions={note.reactions || {}}
                  showAdd={false}
                  onToggle={(key, reacted) => onToggleReaction?.(note.id, key, reacted)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
